/* eslint-disable camelcase */
import React from 'react'
import { css, StyleSheet } from 'aphrodite'
import {
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
} from '@material-ui/core'

import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { UiHelper } from '../../../common/helpers/UiHelper'
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta'
import { AppConfig } from '../../../AppConfig'
import { AppStyles, AppTheme } from '../../../styles/AppTheme'
import { CommonTheme } from '../../../styles/Styles'
import { SearchBar } from '../../../common/components/SearchBar'
import { Controller } from '../../../common/Controller'
import { AppHelper } from '../../../helpers/AppHelper'

import {
  FIELD_TYPE_LABELS,
  STANDARD_CRM_SUBORG_CUSTOM_FIELDS,
  deriveUiKey,
  isValidUiKey,
  buildAddOrgFieldFormDef,
  buildEditOrgFieldFormDef,
} from './TeamsHelper'

export class TeamsFields extends BaseEntityPageSlixta {
  constructor(props) {
    super(props)

    this.ms = AppConfig.CDP_MS.ENDPOINT

    const rawOrgId =
      (props.match && props.match.params && props.match.params.orgId) ||
      AppHelper.parseOrgId(window.location.pathname) ||
      (Controller.get().roleHelper() && Controller.get().roleHelper().getOrgId()) ||
      'PRODUCT'

    const { orgId: parsedParentId } = AppHelper.parseOrgUnitId(rawOrgId)
    this.parentOrgId = parsedParentId || rawOrgId
    this.orgId = this.parentOrgId

    this.pageTitle = Helper.getString('teams-fields', 'Org Custom Fields')
    this.pageKey = 'teams-fields'
    this.noAdd = true
    this.emptyText = 'No custom fields defined yet'

    this.breadCrumbs = [
      {
        title: Helper.getString('backToTeams', 'Back to Teams'),
        to: this.baseRoute() + '/teams',
        type: 'button',
        icon: 'arrow_back_ios',
      },
    ]

    this.toFetch = [
      {
        key: 'orgFields',
        ms: this.ms,
        method: 'GET',
        paths: ['miscs', 'org-custom-fields'],
        queryParams: {
          parentOrgId: this.parentOrgId,
        },
      },
    ]

    this.apiData = {
      orgId: this.orgId,
    }

    this.headerActions = [
      {
        label: 'Add Field',
        onClick: () => this.openAddField(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      },
    ]

    this.tableHeadersMap = {
      'Field Label': current => this.renderLabelCell(current),
      'Variable Key': current => this.renderKeyCell(current),
      'Data Type': current => this.renderTypeCell(current),
      Status: current => this.renderStatusCell(current),
    }

    this.tableHeaders = Object.keys(this.tableHeadersMap)

    this.cellWidths = {
      'Field Label': Styles.cellWidthLabel,
      'Variable Key': Styles.cellWidthKey,
      'Data Type': Styles.cellWidthType,
      Status: Styles.cellWidthStatus,
      actions: Styles.cellWidthActions,
    }

    this.state = {
      ...this.state,
      masterFieldsList: [],
      searchText: '',
      showArchived: true,

      togglingFieldId: null,
      copiedKey: null,
    }
  }

  onFetchSuccess(results) {
    const response = this.getResult(results, 'orgFields') || results[0]

    let itemsList = []
    if (response) {
      if (Array.isArray(response.items)) {
        itemsList = response.items
      } else if (response.items && typeof response.items === 'object') {
        itemsList = Object.keys(response.items).map(key => ({
          id: response.items[key].id || response.items[key].dbKey || key,
          ...response.items[key],
        }))
      }
    }

    // Merge standard fields
    itemsList = itemsList.map(item => {
      const isStd = item.isStandard || STANDARD_CRM_SUBORG_CUSTOM_FIELDS.some(s => s.uiKey === item.uiKey)
      return isStd ? { ...item, isStandard: true } : item
    })
    STANDARD_CRM_SUBORG_CUSTOM_FIELDS.forEach(std => {
      const exists = itemsList.some(item => item.uiKey === std.uiKey)
      if (!exists) {
        itemsList.push({
          id: std.uiKey,
          dbKey: std.uiKey,
          state: 'LIVE',
          ...std,
        })
      }
    })

    itemsList.sort((a, b) => {
      if (a.order != null && b.order != null) return a.order - b.order
      return (a.label || a.uiKey || '').localeCompare(b.label || b.uiKey || '')
    })

    const filtered = this.filterFields(itemsList, this.state.searchText, this.state.showArchived)

    this.setState({
      masterFieldsList: itemsList,
      items: filtered,
    })
  }

  filterFields = (masterList, searchText, showArchived) => {
    let rows = masterList || []
    if (!showArchived) {
      rows = rows.filter(f => (f.state || 'LIVE') === 'LIVE')
    }
    const q = (searchText || '').trim().toLowerCase()
    if (q) {
      rows = rows.filter(f => {
        const labelMatch = (f.label || '').toLowerCase().includes(q)
        const keyMatch = (f.uiKey || '').toLowerCase().includes(q)
        const typeMatch = (f.iType || '').toLowerCase().includes(q)
        return labelMatch || keyMatch || typeMatch
      })
    }
    return rows
  }

  onSearch = (searchText = '') => {
    const items = this.filterFields(
      this.state.masterFieldsList,
      searchText,
      this.state.showArchived
    )
    this.setState({ searchText, items })
  }

  clearSearch = () => {
    const items = this.filterFields(
      this.state.masterFieldsList,
      '',
      this.state.showArchived
    )
    this.setState({ searchText: '', items })
  }

  onToggleShowArchived = (showArchived) => {
    const items = this.filterFields(
      this.state.masterFieldsList,
      this.state.searchText,
      showArchived
    )
    this.setState({ showArchived, items })
  }

  onFormFieldOptionAdd = async ({ formField, value }) => {
    if (!formField.options) {
      formField.options = []
    }
    const trimmed = String(value || '').trim()
    if (!trimmed) return formField.options
    const existing = formField.options.find(
      o => o.key === trimmed || o.label === trimmed || o.text === trimmed
    )
    if (!existing) {
      const newOpt = {
        key: trimmed,
        label: trimmed,
        text: trimmed,
      }
      formField.options.push(newOpt)
    }
    return formField.options
  }

  openAddField = () => {
    const formDef = buildAddOrgFieldFormDef()
    this.formDefinition = formDef
    this.openModalForAdd(formDef)
  }

  openEditField = (field) => {
    const formDef = buildEditOrgFieldFormDef({ field })
    this.formDefinition = formDef

    const prefill = {
      label: field.label || '',
      uiKey: field.uiKey || '',
      iType: field.iType || 'TEXT_SHORT',
      options: (Array.isArray(field.options) ? field.options : []).map(opt =>
        typeof opt === 'object' ? (opt.key || opt.value || opt.label) : opt
      ),
    }
    this.openModalForEdit(prefill, formDef)
  }

  customSubmit = async (options, done) => {
    const { formData, caller } = options
    const dialogType = this.formDefinition && this.formDefinition._dialogType

    if (dialogType === 'addField') {
      const label = (formData.label || '').trim()
      if (!label) {
        caller.setState({
          success: false,
          message: 'Field label is required',
          inProcess: false,
        })
        return
      }

      let uiKey = (formData.uiKey || '').trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_')
      if (!uiKey && label) {
        uiKey = deriveUiKey(label)
      }

      if (!isValidUiKey(uiKey)) {
        caller.setState({
          success: false,
          message: 'Variable Key must be uppercase alphanumeric and underscores (e.g. SUPPORT_PHONE)',
          inProcess: false,
        })
        return
      }

      const isReservedStandard = STANDARD_CRM_SUBORG_CUSTOM_FIELDS.some(
        s => s.uiKey.toUpperCase() === uiKey || s.uiKey.replace(/[^A-Za-z0-9]/g, '').toUpperCase() === uiKey.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      )
      if (isReservedStandard) {
        caller.setState({
          success: false,
          message: `Variable Key "${uiKey}" is reserved for standard fields`,
          inProcess: false,
        })
        return
      }

      const iType = formData.iType || 'TEXT_SHORT'
      const payload = {
        entityAction: 'add',
        label,
        uiKey,
        iType,
        state: 'LIVE',
      }

      if (['SELECT_SINGLE', 'SELECT_MULTI', 'SELECT_ONE'].includes(iType)) {
        payload.options = formData.options || []
      }

      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['settings', `settingscrmorgfields${this.parentOrgId}-all`],
          jsonBody: payload,
        })

        if (response && response.err) {
          caller.setState({
            success: false,
            message: Helper.getErrorMsg(response.err),
            inProcess: false,
          })
          return
        }

        this.showMessage('Custom field added successfully', undefined, 'Success')
        this.setState({ modalOpen: false })
        this.fetchItems()
        if (done) done()
      } catch (err) {
        caller.setState({
          success: false,
          message: Helper.getErrorMsg(err),
          inProcess: false,
        })
      }
    } else if (dialogType === 'editField') {
      const field = this.formDefinition._editingField
      const fieldId = field.id || field.dbKey
      const label = (formData.label || '').trim()

      if (!label) {
        caller.setState({
          success: false,
          message: 'Field label is required',
          inProcess: false,
        })
        return
      }

      const payload = {
        entityAction: 'edit',
        id: fieldId,
        label,
        uiKey: field.uiKey,
        iType: field.iType,
        state: field.state || 'LIVE',
      }

      if (['SELECT_SINGLE', 'SELECT_MULTI', 'SELECT_ONE'].includes(field.iType)) {
        payload.options = formData.options || []
      }

      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['settings', `settingscrmorgfields${this.parentOrgId}-all`],
          jsonBody: payload,
        })

        if (response && response.err) {
          caller.setState({
            success: false,
            message: Helper.getErrorMsg(response.err),
            inProcess: false,
          })
          return
        }

        this.showMessage('Custom field updated successfully', undefined, 'Success')
        this.setState({ modalOpen: false })
        this.fetchItems()
        if (done) done()
      } catch (err) {
        caller.setState({
          success: false,
          message: Helper.getErrorMsg(err),
          inProcess: false,
        })
      }
    }
  }

  handleToggleArchive = async (field) => {
    const fieldId = field.id || field.dbKey
    const isLive = (field.state || 'LIVE') === 'LIVE'
    const newState = isLive ? 'ARCHIVED' : 'LIVE'

    this.setState({ togglingFieldId: fieldId })

    try {
      const response = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'POST',
        paths: ['settings', `settingscrmorgfields${this.parentOrgId}-all`],
        jsonBody: {
          entityAction: 'edit',
          id: fieldId,
          state: newState,
        },
      })

      if (response && response.err) {
        this.showMessage(Helper.getErrorMsg(response.err), undefined, 'Error')
        this.setState({ togglingFieldId: null })
        return
      }

      const actionText = newState === 'ARCHIVED' ? 'archived' : 'activated'
      this.showMessage(`Field '${field.label || field.uiKey}' ${actionText} successfully`, undefined, 'Success')
      this.setState({ togglingFieldId: null })
      this.fetchItems()
    } catch (err) {
      console.error('Failed to toggle archive status:', err)
      const msg =
        (err && (err.errMsg || err.message || err.error || err.description)) ||
        Helper.getErrorMsg(err) ||
        'Failed to update field status'
      this.showMessage(msg, undefined, 'Error')
      this.setState({ togglingFieldId: null })
    }
  }

  handleCopyTemplateKey = (uiKey) => {
    const templateTag = `{{contactorg.${uiKey}}}`
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(templateTag)
      this.setState({ copiedKey: uiKey })
      setTimeout(() => {
        this.setState({ copiedKey: null })
      }, 2000)
    } else {
      Helper.copyToClipboard(templateTag)
      this.showMessage(`Copied ${templateTag} to clipboard`, undefined, 'Copied')
    }
  }

  getTypeBadgeStyle = (iType) => {
    switch (iType) {
      case 'TEXT_SHORT':
        return Styles.typeShortText
      case 'NUMBER':
        return Styles.typeNumber
      case 'DATE_YYYYMMDD':
      case 'DATE':
      case 'DATETIME':
        return Styles.typeDate
      case 'SELECT_SINGLE':
      case 'SELECT_ONE':
        return Styles.typeSelectOne
      case 'SELECT_MULTI':
        return Styles.typeSelectMulti
      case 'TEXT_LONG':
        return Styles.typeLongText
      case 'BOOLEAN':
        return Styles.typeBoolean
      default:
        return Styles.typeDefault
    }
  }

  renderLabelCell(current) {
    const isStandard = Boolean(current.isStandard || current.uiKey === 'locationAddress')
    return (
      <div className={css(Styles.nameCell)}>
        <div className={css(Styles.nameRow)}>
          <span className={css(Styles.line1, Styles.nameTitle)}>
            {current.label || current.uiKey}
          </span>
          {isStandard && (
            <span className={css(Styles.standardBadge)}>Standard</span>
          )}
        </div>
        {current.dbKey && (
          <span className={css(Styles.line2)}>DB: {current.dbKey}</span>
        )}
      </div>
    )
  }

  renderKeyCell(current) {
    const isCopied = this.state.copiedKey === current.uiKey
    const templateTag = `{{contactorg.${current.uiKey}}}`
    return (
      <div className={css(Styles.keyCell)}>
        <div className={css(Styles.keyRow)}>
          <code className={css(Styles.codeKey)}>{current.uiKey}</code>
          <Tooltip
            title={isCopied ? 'Copied template tag!' : `Copy template: ${templateTag}`}
            placement='top-start'
          >
            <IconButton
              size='small'
              onClick={() => this.handleCopyTemplateKey(current.uiKey)}
              className={css(Styles.copyIconButton)}
            >
              <i
                className='material-icons-outlined'
                style={{
                  fontSize: 18,
                  color: isCopied ? '#16A34A' : AppTheme.primaryColor,
                }}
              >
                {isCopied ? 'check_circle' : 'file_copy'}
              </i>
            </IconButton>
          </Tooltip>
        </div>
        <span className={css(Styles.line2)}>
          template: <span className={css(Styles.monoFont)}>{templateTag}</span>
        </span>
      </div>
    )
  }

  renderTypeCell(current) {
    const typeLabel = FIELD_TYPE_LABELS[current.iType] || current.iType || 'Text'
    return (
      <div>
        <span className={css(Styles.typeBadge, this.getTypeBadgeStyle(current.iType))}>
          {typeLabel}
        </span>
        {Array.isArray(current.options) && current.options.length > 0 && (
          <span className={css(Styles.line2)}>
            {current.options.length} options defined
          </span>
        )}
      </div>
    )
  }

  renderStatusCell(current) {
    const isLive = (current.state || 'LIVE') === 'LIVE'
    return (
      <div>
        <span
          className={css(
            Styles.statusBadge,
            isLive ? Styles.statusBadgeSuccess : Styles.statusBadgeDefault
          )}
        >
          {isLive ? 'LIVE' : 'ARCHIVED'}
        </span>
      </div>
    )
  }

  onActions = (current) => {
    const actions = []
    const isLive = (current.state || 'LIVE') === 'LIVE'
    const isStandard = Boolean(current.isStandard || current.uiKey === 'locationAddress')
    const isToggling = this.state.togglingFieldId === (current.id || current.dbKey)

    actions.push(
      <Tooltip title='Edit Label & Options' placement='top-start' key={`${current.id}-edit`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={(e) => {
              e.stopPropagation()
              this.openEditField(current)
            }}
          >
            edit
          </i>
        </span>
      </Tooltip>
    )

    if (!isStandard) {
      actions.push(
        <Tooltip
          title={isLive ? 'Archive Field' : 'Activate Field'}
          placement='top-start'
          key={`${current.id}-archive`}
        >
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i
              className='material-icons-outlined'
              style={{ color: isLive ? '#64748B' : '#16A34A' }}
              onClick={(e) => {
                e.stopPropagation()
                if (!isToggling) {
                  this.handleToggleArchive(current)
                }
              }}
            >
              {isLive ? 'archive' : 'unarchive'}
            </i>
          </span>
        </Tooltip>
      )
    }

    return actions
  }

  onMenuItems = (current) => {
    const isLive = (current.state || 'LIVE') === 'LIVE'
    const isStandard = Boolean(current.isStandard || current.uiKey === 'locationAddress')
    const menuItems = [
      {
        current,
        key: `${current.id}_edit`,
        action: () => this.openEditField(current),
        title: 'Edit Label & Options',
        icon: 'edit',
      },
    ]

    if (!isStandard) {
      menuItems.push({
        current,
        key: `${current.id}_toggle`,
        action: () => this.handleToggleArchive(current),
        title: isLive ? 'Archive Field' : 'Activate Field',
        icon: isLive ? 'archive' : 'unarchive',
      })
    }

    const actions = []
    menuItems.forEach((item) => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  renderAboveTable() {
    return (
      <div className={css(Styles.toolbar)}>
        <div className={css(Styles.searchBarContainer)} onSubmit={e => e.preventDefault()}>
          <SearchBar
            hint='Search by label or variable key'
            onSearch={this.onSearch}
            onClearSearch={this.clearSearch}
            searchText={this.state.searchText}
            searchButtonLabel='Filter'
            clearSearchLabel='Clear'
            autoRefresh
            debounceDuration={1}
            autoFocus={false}
            hideFiltersContainer
          />
        </div>

        <div className={css(Styles.filterSwitchContainer)}>
          <FormControlLabel
            control={
              <Switch
                checked={this.state.showArchived}
                onChange={(e) => this.onToggleShowArchived(e.target.checked)}
                color='primary'
                size='small'
              />
            }
            label={<span className={css(Styles.switchLabel)}>Show Archived</span>}
          />
        </div>
      </div>
    )
  }

  renderBelowTable() {
    return ''
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  searchBarContainer: {
    flex: '1 1 300px',
    minWidth: 0,
    overflowX: 'hidden',
  },
  filterSwitchContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  switchLabel: {
    fontSize: 14,
    color: CommonTheme.textColor,
  },
  cellWidthLabel: {
    width: '26%',
    minWidth: 180,
  },
  cellWidthKey: {
    width: '32%',
    minWidth: 220,
  },
  cellWidthType: {
    width: '20%',
    minWidth: 140,
  },
  cellWidthStatus: {
    width: '12%',
    minWidth: 100,
  },
  cellWidthActions: {
    width: '10%',
    minWidth: 80,
    textAlign: 'right',
  },
  nameCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  standardBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    padding: '1px 6px',
    backgroundColor: '#EFF6FF',
    color: '#2563EB',
    marginLeft: 6,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  nameTitle: {
  },
  line1: {
    fontSize: 16,
    color: CommonTheme.textColor,
  },
  line2: {
    fontSize: 13,
    color: CommonTheme.subTextColor,
    display: 'block',
    marginTop: 2,
  },
  keyCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  keyRow: {
    display: 'flex',
    alignItems: 'center',
  },
  codeKey: {
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    padding: '2px 6px',
    fontWeight: 600,
    fontSize: 13,
    color: '#1E293B',
  },
  copyIconButton: {
    padding: 4,
    marginLeft: 6,
  },
  monoFont: {
  },
  typeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 8px',
    lineHeight: '16px',
  },
  typeShortText: {
    backgroundColor: '#E0F2FE',
    color: '#0369A1',
  },
  typeLongText: {
    backgroundColor: '#E0E7FF',
    color: '#3730A3',
  },
  typeNumber: {
    backgroundColor: '#FEF3C7',
    color: '#B45309',
  },
  typeBoolean: {
    backgroundColor: '#CCFBF1',
    color: '#0F766E',
  },
  typeDate: {
    backgroundColor: '#F3E8FF',
    color: '#6B21A8',
  },
  typeSelectOne: {
    backgroundColor: '#FFEDD5',
    color: '#C2410C',
  },
  typeSelectMulti: {
    backgroundColor: '#FCE7F3',
    color: '#BE185D',
  },
  typeDefault: {
    backgroundColor: '#F1F5F9',
    color: '#475569',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '4px 10px',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  statusBadgeSuccess: {
    backgroundColor: '#3BDE86',
    color: '#222222',
  },
  statusBadgeDefault: {
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
  },
})
